import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { useMonkeysComponentAttributes } from '../../provider/context';
import { cn } from '../base/utils';
import { getOverlaySurfaceClassName } from './internal/overlaySurface';
import { buttonVariants } from './InteractiveControls';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const componentAttributes = useMonkeysComponentAttributes();
  return (
    <DialogPrimitive.Overlay
      {...componentAttributes}
      {...props}
      ref={ref}
      className={cn('theme-overlay-backdrop fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)}
      data-monkeys-component="dialog"
      data-slot="overlay"
      data-monkeys-overlay
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

export interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  showCloseButton?: boolean;
  closeLabel?: React.ReactNode;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
}

const DialogSurfaceContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, ...props }, ref) => {
  const componentAttributes = useMonkeysComponentAttributes();
  return (
    <DialogPrimitive.Content
      {...componentAttributes}
      {...props}
      ref={ref}
      className={className}
      data-monkeys-component="dialog"
      data-slot="content"
      data-surface="dialog"
      data-monkeys-dialog-content
    />
  );
});
DialogSurfaceContent.displayName = 'DialogSurfaceContent';

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, showCloseButton = true, closeLabel = 'Close', overlayClassName, overlayStyle, ...props }, ref) => {
    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} style={overlayStyle} />
        <DialogSurfaceContent
          {...props}
          ref={ref}
          className={cn('fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', getOverlaySurfaceClassName('dialog'), className)}
        >
          {children}
          {showCloseButton ? (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-dialog-surface transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X aria-hidden="true" className="h-4 w-4" />
              <span className="sr-only">{closeLabel}</span>
            </DialogPrimitive.Close>
          ) : null}
        </DialogSurfaceContent>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = 'DialogContent';

export const DialogContentNoAnimation = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, ...props }, ref) => <DialogContent ref={ref} className={cn('duration-0 data-[state=open]:animate-none data-[state=closed]:animate-none', className)} {...props} />,
);
DialogContentNoAnimation.displayName = 'DialogContentNoAnimation';

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';
export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';
export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />);
DialogTitle.displayName = 'DialogTitle';
export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
DialogDescription.displayName = 'DialogDescription';

export const AlertDialog: React.FC<React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Root>> = ({ onOpenChange, open, ...props }) => {
  React.useEffect(() => {
    if (open !== false || typeof document === 'undefined') return undefined;
    const cleanup = () => {
      if (!document.querySelector('[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]')) {
        document.body.style.pointerEvents = '';
      }
    };
    requestAnimationFrame(cleanup);
    const timer = window.setTimeout(cleanup, 300);
    return () => window.clearTimeout(timer);
  }, [open]);
  const handleOpenChange = React.useCallback((next: boolean) => {
    onOpenChange?.(next);
    if (!next && typeof document !== 'undefined') requestAnimationFrame(() => {
      if (!document.querySelector('[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]')) document.body.style.pointerEvents = '';
    });
  }, [onOpenChange]);
  return <AlertDialogPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />;
};
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;
export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => {
  const componentAttributes = useMonkeysComponentAttributes();
  return <AlertDialogPrimitive.Overlay {...componentAttributes} {...props} ref={ref} className={cn('fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} data-monkeys-component="alert-dialog" data-slot="overlay" data-monkeys-overlay style={{ pointerEvents: 'auto', ...style }} />;
});
AlertDialogOverlay.displayName = 'AlertDialogOverlay';
export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, style, ...props }, ref) => {
  const componentAttributes = useMonkeysComponentAttributes();
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content {...componentAttributes} {...props} ref={ref} className={cn('fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', getOverlaySurfaceClassName('dialog'), className)} data-monkeys-component="alert-dialog" data-slot="content" data-surface="dialog" data-monkeys-dialog-content style={{ pointerEvents: 'auto', ...style }} />
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = 'AlertDialogContent';
export const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />;
export const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />;
export const AlertDialogTitle = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold', className)} {...props} />);
export const AlertDialogDescription = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
export const AlertDialogAction = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Action>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />);
export const AlertDialogDangerAction = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Action>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants({ variant: 'destructive' }), className)} {...props} />);
export const AlertDialogCancel = React.forwardRef<React.ElementRef<typeof AlertDialogPrimitive.Cancel>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Cancel ref={ref} className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0', className)} {...props} />);

AlertDialogHeader.displayName = 'AlertDialogHeader';
AlertDialogFooter.displayName = 'AlertDialogFooter';
AlertDialogTitle.displayName = 'AlertDialogTitle';
AlertDialogDescription.displayName = 'AlertDialogDescription';
AlertDialogAction.displayName = 'AlertDialogAction';
AlertDialogDangerAction.displayName = 'AlertDialogDangerAction';
AlertDialogCancel.displayName = 'AlertDialogCancel';
